import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { apiGet, apiPost } from './api';
import { STORES_API_BASE, isNotAvailable } from './useStores';

export type PulseItem = {
  item_no: string;
  descr: string;
  qty_on_hnd: number;
  price: number | null;
};

// Wire rows for /top-items differ from the search rows (7-day sales sums).
export type TopItemRow = {
  item_no: string;
  descr: string;
  total_qty: number;
  total_sales: number;
};

export type PulseSignal = 'too_much' | 'send_more' | 'requested' | 'problem' | 'other';

export type PulseFlag = {
  id: number;
  item_no: string;
  descr: string;
  signal: PulseSignal;
  note: string | null;
  created_at: string;
  /** 'open' until HQ acknowledges it in Cockpit. Absent on an older backend. */
  status?: 'open' | 'acknowledged' | string;
  acknowledged_at?: string | null;
};

export type TopItemsResponse = {
  top: TopItemRow[];
  bottom: TopItemRow[];
};

export function useItemSearch(q: string, storeNo?: number): UseQueryResult<PulseItem[], Error> {
  const qs = new URLSearchParams({ q });
  if (storeNo !== undefined) qs.set('store_no', String(storeNo));
  return useQuery<PulseItem[], Error>({
    queryKey: ['stores', 'inventory', 'items', q, storeNo ?? 'own'],
    queryFn: async () => {
      try {
        const r = await apiGet<{ items: Array<{ item_no: string; descr: string | null; qty_on_hnd: number | null; price: number | null }> }>(
          `${STORES_API_BASE}/inventory/items?${qs.toString()}`,
        );
        return r.items.map((it) => ({
          item_no: it.item_no,
          descr: it.descr ?? it.item_no,
          qty_on_hnd: it.qty_on_hnd ?? 0,
          price: it.price,
        }));
      } catch (err) {
        if (isNotAvailable(err)) return [];
        throw err;
      }
    },
    enabled: q.length >= 2,
    staleTime: 0,
    retry: false,
  });
}

function flagsQKey(storeNo?: number) {
  return ['stores', 'pulse', 'flags', storeNo ?? 'own'] as const;
}

export function usePulseFlags(storeNo?: number): UseQueryResult<PulseFlag[], Error> {
  const qs = storeNo !== undefined ? `?store_no=${storeNo}` : '';
  return useQuery<PulseFlag[], Error>({
    queryKey: flagsQKey(storeNo),
    queryFn: async () => {
      try {
        const r = await apiGet<{ pulses: Array<Omit<PulseFlag, 'descr'> & { descr: string | null }> }>(
          `${STORES_API_BASE}/pulse${qs}`,
        );
        return r.pulses.map((p) => ({ ...p, descr: p.descr ?? p.item_no }));
      } catch (err) {
        if (isNotAvailable(err)) return [];
        throw err;
      }
    },
    staleTime: 30_000,
    retry: false,
  });
}

export function useSubmitFlag(storeNo?: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { item_no: string; signal: PulseSignal; note?: string }) =>
      apiPost<void>(`${STORES_API_BASE}/pulse`, {
        ...body,
        ...(storeNo !== undefined ? { store_no: String(storeNo) } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: flagsQKey(storeNo) });
    },
  });
}

export async function lookupBarcodeItem(barcode: string, storeNo?: number): Promise<PulseItem | null> {
  const qs = new URLSearchParams({ barcode });
  if (storeNo !== undefined) qs.set('store_no', String(storeNo));
  const r = await apiGet<{
    found: boolean;
    item: { item_no: string; descr: string | null; qty_on_hnd: number | null; price: number | null } | null;
  }>(`${STORES_API_BASE}/inventory/item?${qs.toString()}`);
  if (!r.found || !r.item) return null;
  return {
    item_no: r.item.item_no,
    descr: r.item.descr ?? r.item.item_no,
    qty_on_hnd: r.item.qty_on_hnd ?? 0,
    price: r.item.price,
  };
}

export function useTopItems(storeNo?: number): UseQueryResult<TopItemsResponse | null, Error> {
  const qs = storeNo !== undefined ? `?store_no=${storeNo}` : '';
  return useQuery<TopItemsResponse | null, Error>({
    queryKey: ['stores', 'pulse', 'top-items', storeNo ?? 'own'],
    queryFn: async () => {
      try {
        const r = await apiGet<{ top: Array<TopItemRow & { descr: string | null }>; bottom: Array<TopItemRow & { descr: string | null }> }>(
          `${STORES_API_BASE}/top-items${qs}`,
        );
        const norm = (rows: Array<TopItemRow & { descr: string | null }>) =>
          rows.map((t) => ({ ...t, descr: t.descr ?? t.item_no }));
        return { top: norm(r.top), bottom: norm(r.bottom) };
      } catch (err) {
        if (isNotAvailable(err)) return null;
        throw err;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
}
